// buildVersion.js — which build this tab is running, and whether a newer one
// has been deployed since it was opened.
//
// The app is a single page: a tab opened before a deploy keeps running the
// JavaScript it first loaded (calculators, quote template, PDF) until it's
// reloaded. So every few minutes, and whenever the tab comes back into view,
// this re-reads the site's index.html and compares its main script with the
// one this tab loaded; if they differ, a banner asks for a reload.
import React, { useEffect, useState } from "react";

// Commit the build came from: the build script passes Vercel's
// VERCEL_GIT_COMMIT_SHA (see package.json), and Vercel also exposes it to
// Create React App builds as REACT_APP_VERCEL_GIT_COMMIT_SHA. "local" otherwise.
export const BUILD_SHA = (process.env.REACT_APP_BUILD_SHA || process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || "").slice(0, 7) || "local";

const MAIN_SCRIPT = /\/static\/js\/main\.[0-9a-f]+\.js/;
const CHECK_EVERY_MS = 5 * 60 * 1000;

// The main bundle this tab is running, e.g. "/static/js/main.ffac5062.js".
function runningBundle() {
  const el = Array.from(document.scripts).find(s => MAIN_SCRIPT.test(s.src || ""));
  return el ? el.src.match(MAIN_SCRIPT)[0] : null;
}

// True once the deployed index.html points at a different main bundle.
export function useNewVersionAvailable() {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    const mine = runningBundle();
    if (!mine) return undefined; // dev server: no hashed bundle to compare
    let stopped = false;
    const check = async () => {
      if (stopped || document.visibilityState !== "visible") return;
      try {
        const res = await fetch("/", { cache: "no-store" });
        const live = (await res.text()).match(MAIN_SCRIPT)?.[0];
        if (live && live !== mine) setAvailable(true);
      } catch (e) { /* offline: try again next time */ }
    };
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", check);
    check();
    return () => { stopped = true; clearInterval(timer); document.removeEventListener("visibilitychange", check); };
  }, []);
  return available;
}

// Bottom banner shown when a newer build is live. Calculators autosave their
// drafts, so reloading keeps unsaved work.
export function NewVersionBanner() {
  const available = useNewVersionAvailable();
  if (!available) return null;
  return (
    <div role="status" style={{ position: "fixed", left: "50%", bottom: "calc(16px + env(safe-area-inset-bottom, 0px))", transform: "translateX(-50%)", zIndex: 1500, width: "min(560px, calc(100% - 32px))", background: "#FFF3D6", border: "2px solid #F5A623", borderRadius: 10, padding: "12px 16px", display: "flex", gap: 12, alignItems: "center", boxShadow: "0 6px 24px rgba(0,0,0,0.35)", color: "#3a2a00", fontSize: 14 }}>
      <span style={{ flex: 1 }}>A newer version of the app has been deployed. Reload to use it; your unsaved quote is kept.</span>
      <button type="button" onClick={() => window.location.reload()} style={{ background: "#1D3461", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 600, cursor: "pointer" }}>Reload</button>
    </div>
  );
}

// Small build label, preview deployments only, so a tester can see which
// commit the tab is running.
export function BuildStamp() {
  return (
    <div title="The commit this tab is running" style={{ position: "fixed", left: 8, bottom: "calc(6px + env(safe-area-inset-bottom, 0px))", zIndex: 1400, fontSize: 10, color: "#8a95a3", background: "rgba(255,255,255,0.85)", borderRadius: 4, padding: "1px 6px", pointerEvents: "none", fontFamily: "monospace" }}>
      Build {BUILD_SHA}
    </div>
  );
}
